import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import RichText from '~/components/common/rich-text';
import Icon from '~/components/common/icon';
import { updateQuestion } from '~/api';

/**
 * Settings for each question
 */
class QuestionSettings extends PureComponent {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		this.state = {
			correctMessage: this.props.question.correctMsg,
			incorrectMessage: this.props.question.incorrectMsg,
			useSameMessage: this.props.question.correctSameText,
			solutionHint: this.props.question.tipEnabled,
			solutionHintMessage: this.props.question.tipMsg,
			is_loading: false,
			error: null,
		};

		this.handleCorrectMessage = this.handleCorrectMessage.bind( this );
		this.handleIncorrectMessage = this.handleIncorrectMessage.bind( this );
		this.handleCheckChange = this.handleCheckChange.bind( this );
		this.handleSolutionHintMessage = this.handleSolutionHintMessage.bind(
			this
		);
		this.handleSaveSettings = this.handleSaveSettings.bind( this );
	}

	/**
	 * Handle all checkbox changes
	 *
	 * @param {handleCheckChange} event
	 */
	handleCheckChange( event ) {
		const target = event.target;
		const name = target.name;

		this.setState( ( prevState ) => ( {
			[ name ]: ! prevState[ name ],
		} ) );
	}

	/**
	 * Listener when we update the correct answer text
	 *
	 * @param {string} value
	 */
	handleCorrectMessage( value ) {
		this.setState( {
			correctMessage: value,
		} );
	}

	/**
	 * Listener when we update the incorrect answer text
	 *
	 * @param {string} value
	 */
	handleIncorrectMessage( value ) {
		this.setState( {
			incorrectMessage: value,
		} );
	}

	/**
	 * Listener when we update the solution hint message
	 *
	 * @param {string} value
	 */
	handleSolutionHintMessage( value ) {
		this.setState( {
			solutionHintMessage: value,
		} );
	}

	/**
	 * Listener when saving the settings
	 *
	 * @param {handleSaveSettings} e
	 */
	async handleSaveSettings( e ) {
		e.persist();
		e.preventDefault();
		const {
			correctMessage,
			incorrectMessage,
			useSameMessage,
			solutionHint,
			solutionHintMessage,
		} = this.state;

		const res = await updateQuestion( this.props.question.ID, {
			_correctMsg: correctMessage,
			_incorrectMsg: incorrectMessage,
			_correctSameText: useSameMessage,
			_tipEnabled: solutionHint,
			_tipMsg: solutionHintMessage,
		} );

		if ( 200 === res ) {
			this.props.handleSettingsForm( e );
		}
	}

	/**
	 * Listener when component unmounts
	 */
	componentWillUnmount() {
		this.props.unlockQuestion();
	}

	/**
	 * Render the Question Settings
	 *
	 * @event handleCheckChange
	 * @event handleSaveSettings
	 */
	render() {
		const { showSettings, question, handleSettingsForm } = this.props;
		const {
			correctMessage,
			incorrectMessage,
			useSameMessage,
			solutionHint,
			solutionHintMessage,
			is_loading,
			error,
		} = this.state;

		return (
			<div
				className={ `ld__builder--question-settings ${
					showSettings ? '-editing' : ''
				}` }
			>
				{ ! showSettings && (
					<button
						className="ld__builder--show-settings ld-button-reset"
						onClick={ handleSettingsForm }
					>
						{ `${ LearnDashData.labels.question } ${ LearnDashData.i18n.settings }` }{ ' ' }
						<Icon icon="dropdown" />
					</button>
				) }

				{ showSettings && (
					<Fragment>
						<hr />
						<h4>{ `${ LearnDashData.i18n.correct_answer_message }` }</h4>

						<RichText
							id={ `text-area-question-correct-${ question.ID }` }
							value={ correctMessage }
							onChange={ this.handleCorrectMessage }
						/>

						<div className="ld__builder--question-label-value">
							<div>
								<label
									htmlFor={ `check-same-message-${ question.ID }` }
								>{ `${ LearnDashData.i18n.different_incorrect_answer_message }` }</label>
							</div>
							<div>
								<div className="ld-switch-wrapper">
									<span className="ld-switch">
										<input
											id={ `check-same-message-${ question.ID }` }
											type="checkbox"
											name="useSameMessage"
											checked={ ! useSameMessage }
											onChange={ this.handleCheckChange }
											className="ld-switch__input"
										/>
										<span className="ld-switch__track"></span>
										<span className="ld-switch__thumb"></span>
										<span className="ld-switch__on-off"></span>
									</span>
								</div>
								{ useSameMessage && (
									<span>{ `${ LearnDashData.i18n.same_answer_message }` }</span>
								) }
							</div>
						</div>

						{ ! useSameMessage && (
							<Fragment>
								<h4>{ `${ LearnDashData.i18n.incorrect_answer_message }` }</h4>
								<RichText
									id={ `text-area-question-incorrect-${ question.ID }` }
									value={ incorrectMessage }
									onChange={ this.handleIncorrectMessage }
								/>
							</Fragment>
						) }

						<div className="ld__builder--question-label-value">
							<div>
								<label
									htmlFor={ `check-solution-hint-${ question.ID }` }
								>{ `${ LearnDashData.i18n.solution_hint }` }</label>
							</div>
							<div>
								<div className="ld-switch-wrapper">
									<span className="ld-switch">
										<input
											id={ `check-solution-hint-${ question.ID }` }
											type="checkbox"
											name="solutionHint"
											checked={ solutionHint }
											onChange={ this.handleCheckChange }
											className="ld-switch__input"
										/>
										<span className="ld-switch__track"></span>
										<span className="ld-switch__thumb"></span>
										<span className="ld-switch__on-off"></span>
									</span>
								</div>
							</div>
						</div>

						{ solutionHint && (
							<RichText
								id={ `text-area-hint-${ question.ID }` }
								value={ solutionHintMessage }
								onChange={ this.handleSolutionHintMessage }
							/>
						) }

						<div className="ld__builder--settings-actions">
							<button
								className="is-primary"
								onClick={ this.handleSaveSettings }
							>{ `${ LearnDashData.i18n.save }` }</button>
							<button
								className="is-default"
								onClick={ handleSettingsForm }
							>{ `${ LearnDashData.i18n.cancel }` }</button>

							{ is_loading && (
								<span className="ld__builder--form-notice">{ `${ LearnDashData.i18n.please_wait }` }</span>
							) }
							{ error && (
								<span className="ld__builder--form-error">
									{ error }
								</span>
							) }
						</div>
					</Fragment>
				) }
			</div>
		);
	}
}

/**
 * Valid props
 */
QuestionSettings.propTypes = {
	showSettings: PropTypes.bool,
	question: PropTypes.object,
	handleSettingsForm: PropTypes.func,
	unlockQuestion: PropTypes.func,
};

export default QuestionSettings;
